import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// --- SUB-COMPONENT FOR INLINE EDITING ---
const RoleItem = ({ role, onEdit, onDelete, onSave, onCancel, isEditing, editData, setEditData }) => {
    
    // Display Mode
    if (!isEditing) {
        return (
            <div className="p-4 group flex justify-between items-start hover:bg-slate-50 transition-colors">
                <div>
                    <p className="font-bold text-slate-800">{role.role_name}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {role.custom_levels && role.custom_levels.split(',').map((l, i) => (
                            <span key={i} className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">{l.trim()}</span>
                        ))}
                        {role.custom_options && role.custom_options.split(',').map((s, i) => (
                            <span key={i} className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">{s.trim()}</span>
                        ))}
                    </div>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => onEdit(role)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-100 rounded-full" title="Edit"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg></button>
                    <button onClick={() => onDelete(role.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-100 rounded-full" title="Delete"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                </div>
            </div>
        );
    }

    // Editing Mode
    return (
        <div className="p-4 bg-blue-50 border-t-2 border-b-2 border-blue-200">
            <div className="space-y-4">
                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Role Name</label>
                    <input type="text" value={editData.role_name} onChange={e => setEditData({...editData, role_name: e.target.value})} className="w-full p-2 border rounded-md"/>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={editData.has_level} onChange={e => setEditData({...editData, has_level: e.target.checked})} /> Has Levels?</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={editData.has_subject} onChange={e => setEditData({...editData, has_subject: e.target.checked})} /> Has Subjects?</label>
                </div>
                {editData.has_level && (
                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase">Levels (comma separated)</label>
                        <input type="text" placeholder="e.g. PGT, TGT, PRT" value={editData.custom_levels} onChange={e => setEditData({...editData, custom_levels: e.target.value})} className="w-full p-2 border rounded-md"/>
                    </div>
                )}
                {editData.has_subject && (
                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase">Subjects (comma separated)</label>
                        <input type="text" placeholder="e.g. Maths, Physics" value={editData.custom_options} onChange={e => setEditData({...editData, custom_options: e.target.value})} className="w-full p-2 border rounded-md"/>
                    </div>
                )}
                <div className="flex gap-2">
                    <button onClick={() => onSave(role.id)} className="flex-1 bg-blue-600 text-white py-2 rounded-md font-bold">Save</button>
                    <button onClick={onCancel} className="flex-1 bg-slate-200 py-2 rounded-md">Cancel</button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const FormConfigTab = () => {
    const [configs, setConfigs] = useState([]);
    const [categoryOrder, setCategoryOrder] = useState([]);

    // State for the main "Add New Role" form
    const [newRoleData, setNewRoleData] = useState({ category: '', role_name: '' });

    // State for inline editing
    const [editingRoleId, setEditingRoleId] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    // Fetch data on load
    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = () => {
        fetch('/api/school/config')
            .then(res => res.json())
            .then(data => {
                setConfigs(data);
                const uniqueCategories = [...new Set(data.sort((a,b) => a.category_sequence - b.category_sequence).map(item => item.category))];
                setCategoryOrder(uniqueCategories);
            });
    };

    const groupedConfigs = configs.reduce((acc, item) => {
        if (!acc[item.category]) acc[item.category] = [];
        acc[item.category].push(item);
        acc[item.category].sort((a, b) => a.sequence - b.sequence);
        return acc;
    }, {});

    // --- API HANDLERS ---
    const handleAddRole = async () => {
        if (!newRoleData.category || !newRoleData.role_name) return alert("Please select a category and enter a role name.");
        
        const payload = {
            ...newRoleData,
            has_level: false,
            has_subject: false,
            custom_levels: '',
            custom_options: ''
        };
        
        await fetch('/api/admin/config/role', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(payload)
        });

        fetchConfig();
        setNewRoleData({ category: newRoleData.category, role_name: '' }); // Keep category, reset role
    };

    const handleUpdateRole = async (roleId) => {
        const payload = {
            ...editFormData,
            has_level: editFormData.has_level ? 1 : 0,
            has_subject: editFormData.has_subject ? 1 : 0
        };

        await fetch(`/api/admin/config/role/${roleId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(payload)
        });
        
        setEditingRoleId(null);
        fetchConfig();
    };

    const handleDeleteRole = async (roleId) => {
        if (!window.confirm("Are you sure you want to delete this role?")) return;
        await fetch(`/api/admin/config/role/${roleId}`, { method: 'DELETE' });
        fetchConfig();
    };

    // --- DRAG & DROP HANDLER ---
    const handleDragEnd = async (result) => {
        // ... (Your existing drag-and-drop logic remains unchanged)
    };
    
    // --- EDITING STATE HANDLERS ---
    const startEditing = (role) => {
        setEditingRoleId(role.id);
        setEditFormData({
            category: role.category,
            role_name: role.role_name,
            has_level: role.has_level === 1,
            has_subject: role.has_subject === 1,
            custom_levels: role.custom_levels || '',
            custom_options: role.custom_options || ''
        });
    };
    const cancelEditing = () => {
        setEditingRoleId(null);
    };

    return (
        <div className="space-y-8">
            {/* --- SECTION 1: ADD NEW ROLE --- */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <h3 className="font-black text-slate-800 uppercase mb-4 text-lg">Add a New Role</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-1">
                        <label className="text-xs font-bold text-slate-500 uppercase">1. Select Category</label>
                        <select 
                            value={newRoleData.category}
                            onChange={e => setNewRoleData({...newRoleData, category: e.target.value})}
                            className="w-full p-3 border border-slate-300 rounded-lg mt-1 text-sm font-bold focus:ring-2 focus:ring-orange-500 outline-none"
                        >
                            <option value="" disabled>Choose category...</option>
                            {categoryOrder.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                        <p className="text-[10px] text-slate-400 mt-1">To add a new category, type it below.</p>
                         <input 
                            type="text" 
                            placeholder="Or, type a NEW category" 
                            onBlur={e => e.target.value && setNewRoleData({...newRoleData, category: e.target.value})} 
                            className="w-full p-2 border border-slate-200 rounded-lg mt-2 text-xs font-bold"
                        />
                    </div>
                    <div className="md:col-span-2 flex items-end gap-2">
                        <div className="flex-1">
                             <label className="text-xs font-bold text-slate-500 uppercase">2. Enter Role Name</label>
                            <input 
                                type="text"
                                placeholder="e.g. Science Teacher"
                                value={newRoleData.role_name}
                                onChange={e => setNewRoleData({...newRoleData, role_name: e.target.value})}
                                className="w-full p-3 border border-slate-300 rounded-lg mt-1 text-sm font-bold focus:ring-2 focus:ring-orange-500 outline-none"
                            />
                        </div>
                        <button onClick={handleAddRole} className="bg-slate-800 text-white h-[46px] px-6 rounded-lg font-bold hover:bg-slate-900 transition-colors text-sm">+ Add</button>
                    </div>
                </div>
            </div>

            {/* --- SECTION 2: MANAGE EXISTING ROLES --- */}
            <div>
                 <h3 className="font-black text-slate-800 uppercase mb-4 text-lg">Manage Roles</h3>
                 <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="all-categories" type="category">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                                {categoryOrder.map((category, index) => (
                                    <Draggable key={category} draggableId={category} index={index}>
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.draggableProps} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                                                <div {...provided.dragHandleProps} className="bg-slate-100 px-4 py-3 border-b border-slate-200 flex justify-between items-center cursor-move">
                                                    <h4 className="font-bold text-slate-700 uppercase tracking-wide">{category}</h4>
                                                    <span className="text-xs font-semibold text-slate-500">{groupedConfigs[category]?.length || 0} Roles</span>
                                                </div>
                                                <Droppable droppableId={category} type="role">
                                                    {(provided) => (
                                                        <div {...provided.droppableProps} ref={provided.innerRef} className="divide-y divide-slate-100">
                                                            {groupedConfigs[category]?.map((item, index) => (
                                                                <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                                                                    {(provided) => (
                                                                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                                            <RoleItem 
                                                                                role={item}
                                                                                isEditing={editingRoleId === item.id}
                                                                                editData={editFormData}
                                                                                setEditData={setEditFormData}
                                                                                onEdit={startEditing}
                                                                                onCancel={cancelEditing}
                                                                                onSave={handleUpdateRole}
                                                                                onDelete={handleDeleteRole}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            ))}
                                                            {provided.placeholder}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                 </DragDropContext>
            </div>
        </div>
    );
};

export default FormConfigTab;